import { useRef, useMemo, useState, useEffect } from 'react';
import qs from 'qs';

const useParams = () => {
  const updateQs = useRef(false);
  const { q, tab, page, s, ...filters } = useMemo(() => {
    const { q, tab, page, s, ...filters } = qs.parse(location.search, {
      ignoreQueryPrefix: true
    });
    Object.keys(filters).forEach(filter => {
      if (typeof filters[filter] === 'string') {
        filters[filter] = [filters[filter]];
      }
    });
    return { q, tab, page, s, ...filters };
  }, []);

  const [params, setParams] = useState({
    query: q,
    tab,
    page: page || 1,
    sort: s,
    filters
  });

  useEffect(() => {
    const buildQueryString = () => {
      const qsParams = {};

      if (params.query) {
        qsParams.q = params.query;
      }

      if (params.tab) {
        qsParams.tab = params.tab;
      }

      if (params.page && params.page > 1) {
        qsParams.page = params.page;
      }

      if (params.sort) {
        qsParams.s = params.sort;
      }

      if (params.filters) {
        Object.keys(params.filters).forEach(key => {
          qsParams[key] = params.filters[key];
        });
      }

      const qsValue = qs.stringify(qsParams);

      const newUrl = `${location.protocol}//${location.host}${
        location.pathname
      }${qsValue ? `?${qsValue}` : ''}`;

      window.history.pushState(params, '', newUrl);
    };

    if (updateQs.current) {
      buildQueryString();
    } else {
      updateQs.current = true;
    }
  }, [params]);

  useEffect(() => {
    const handlePopState = () => {
      const qsParams = qs.parse(location.search, { ignoreQueryPrefix: true });
      updateQs.current = false;

      if (qsParams.q) {
        params.query = qsParams.q;
      } else {
        params.query = '';
      }

      if (qsParams.tab) {
        params.tab = qsParams.tab;
      }

      if (qsParams.page) {
        params.page = qsParams.page;
      }

      if (qsParams.sort) {
        params.sort = qsParams.s;
      }

      Object.keys(params.filters).forEach(key => {
        params.filters[key] = qsParams[key];
      });

      setParams({ ...params });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const setQuery = query => {
    setParams({ ...params, query, page: 1 });
  };

  const setTab = tab => {
    setParams({ ...params, tab, page: 1 });
  };

  const setPage = page => {
    setParams({ ...params, page });
  };

  const setSort = sort => {
    setParams({ ...params, sort, page: 1 });
  };

  const setFilters = filters => {
    setParams({ ...params, filters, page: 1 });
  };

  return [params, setQuery, setTab, setPage, setSort, setFilters];
};

export default useParams;
